import React, { useState, useRef } from "react";
import Helmet from "react-helmet";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Path from "routes/Path/index";
import { FORGOT_PASSWORD } from "common/viewContent";
import { forgotPassword, verifyOTP, resetPassword } from "store/actions";
import FormikForm from "components/shared/FormikForm";
import { EmailValidation, OtpValidation, PasswordValidation } from "./Validation";
import { EmailFields, PasswordFields } from "./inputFields";

const STEP = {
  EMAIL: "EMAIL",
  OTP: "OTP",
  PASSWORD: "PASSWORD",
};

const OTP_LENGTH = 6;

const OtpDigitInput = ({ value, onChange, error }) => {
  const inputsRef = useRef([]);
  const digits = value ? value.split("") : [];

  const setDigitAt = (index, digit) => {
    const next = [...Array(OTP_LENGTH)].map((_, i) => digits[i] || "");
    next[index] = digit;
    onChange(next.join(""));
  };

  const handleChange = (e, index) => {
    const val = e.target.value.replace(/[^0-9]/g, "");
    if (!val) {
      setDigitAt(index, "");
      return;
    }
    const char = val[val.length - 1];
    setDigitAt(index, char);
    if (index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (digits[index]) {
        setDigitAt(index, "");
      } else if (index > 0) {
        inputsRef.current[index - 1]?.focus();
        setDigitAt(index - 1, "");
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, OTP_LENGTH);
    if (pasted) {
      onChange(pasted.padEnd(OTP_LENGTH, "").slice(0, OTP_LENGTH).trimEnd());
      const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
      inputsRef.current[focusIndex]?.focus();
    }
  };

  return (
    <div className="mb-3">
      <label className="fw-bold d-block mb-2">OTP</label>
      <div className="d-flex" style={{ gap: "10px" }}>
        {[...Array(OTP_LENGTH)].map((_, index) => (
          <input
            key={index}
            ref={(el) => (inputsRef.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digits[index] || ""}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            className={`form-control text-center${error ? " is-invalid" : ""}`}
            style={{
              flex: 1,
              minWidth: 0,
              height: "56px",
              fontSize: "1.5rem",
              fontWeight: 600,
              color: "#1a1a2e",
              background: "#fff",
              border: "1px solid #ced4da",
              borderRadius: "8px",
            }}
            autoFocus={index === 0}
          />
        ))}
      </div>
      {error && <div className="text-danger small mt-1">{error}</div>}
    </div>
  );
};

const ForgotPassword = ({ submitting }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading } = useSelector((state) => state.Auth);

  const [step, setStep] = useState(STEP.EMAIL);
  const [context, setContext] = useState({
    email: "",
    userId: "",
    resetToken: "",
  });
  const [apiError, setApiError] = useState("");

  const handleEmailSubmit = async (values, { setSubmitting }) => {
    setApiError("");
    try {
      await dispatch(forgotPassword({ data: { email: values.email } })).unwrap();
      setContext((prev) => ({ ...prev, email: values.email }));
      setStep(STEP.OTP);
    } catch (err) {
      console.error(err);
      setApiError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOtpSubmit = async (values, { setSubmitting }) => {
    setApiError("");
    try {
      const res = await dispatch(
        verifyOTP({ data: { email: context.email, otp: values.otp } })
      ).unwrap();

      setContext((prev) => ({
        ...prev,
        userId: res?.userId,
        resetToken: res?.resetToken,
      }));
      setStep(STEP.PASSWORD);
    } catch (err) {
      console.error(err);
      setApiError(err?.message || "Invalid or expired OTP. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (values, { resetForm, setSubmitting }) => {
    setApiError("");
    try {
      await dispatch(
        resetPassword({
          data: {
            userId: context.userId,
            resetToken: context.resetToken,
            newPassword: values.newPassword,
            confirmPassword: values.confirmPassword,
          },
        })
      ).unwrap();
      resetForm();
      navigate("/login");
    } catch (err) {
      console.error(err);
      setApiError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const stepSubText = {
    [STEP.EMAIL]: "Please enter your email to reset your password",
    [STEP.OTP]: "Please enter the OTP sent to your email",
    [STEP.PASSWORD]: "Please enter your new password",
  };

  const stepConfig = {
    [STEP.EMAIL]: {
      initialValues: { email: context.email },
      validationSchema: EmailValidation,
      inputFields: EmailFields,
      onSubmit: handleEmailSubmit,
      buttonText: FORGOT_PASSWORD.CONTINUE,
    },
    [STEP.PASSWORD]: {
      initialValues: { newPassword: "", confirmPassword: "" },
      validationSchema: PasswordValidation,
      inputFields: PasswordFields,
      onSubmit: handlePasswordSubmit,
      buttonText: FORGOT_PASSWORD.SAVE,
    },
  };

  const handleBackClick = () => {
    if (step === STEP.OTP) {
      setStep(STEP.EMAIL);
    } else if (step === STEP.PASSWORD) {
      setStep(STEP.OTP);
    } else {
      navigate("/login");
    }
  };

  return (
    <>
      <Helmet title={FORGOT_PASSWORD.CURRENT_MODULE} />
      <div className="container mt-5" style={{ overflow: "hidden" }}>
        <div className="row">
          <div className="col-12 col-sm-8 offset-sm-2 col-md-6 offset-md-3 col-lg-6 offset-lg-3 col-xl-4 offset-xl-4">
            <div className="login-brand"></div>
            <div className="card card-primary">
              <div className="card-header d-flex align-items-center position-relative">
                <button
                  type="button"
                  onClick={handleBackClick}
                  className="btn btn-link p-0 position-absolute"
                  style={{
                    left: "1rem",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#6c757d",
                    display: "flex",
                    alignItems: "center",
                  }}
                  aria-label="Back"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 12H5" />
                    <path d="M12 19l-7-7 7-7" />
                  </svg>
                </button>
                <h4 className="w-100 text-center mb-0">
                  {FORGOT_PASSWORD.CURRENT_MODULE}
                </h4>
              </div>
              <div className="card-body text-center">
                <p className="text-muted">{stepSubText[step]}</p>
                {apiError && (
                  <div className="alert alert-danger py-2 text-left">{apiError}</div>
                )}

                {step === STEP.OTP ? (
                  <div className="text-left">
                    <Formik
                      initialValues={{ otp: "" }}
                      validationSchema={OtpValidation}
                      onSubmit={handleOtpSubmit}
                    >
                      {({ values, errors, touched, setFieldValue, isSubmitting }) => (
                        <Form>
                          <OtpDigitInput
                            value={values.otp}
                            onChange={(val) => setFieldValue("otp", val)}
                            error={touched.otp ? errors.otp : ""}
                          />
                          <button
                            type="submit"
                            className="btn btn-primary d-block"
                            style={{ width: "100%" }}
                            disabled={isSubmitting || loading}
                          >
                            {FORGOT_PASSWORD.CONTINUE}
                          </button>
                        </Form>
                      )}
                    </Formik>
                  </div>
                ) : (
                  <div className="text-left">
                    <FormikForm
                      key={step}
                      initialValues={stepConfig[step].initialValues}
                      validationSchema={stepConfig[step].validationSchema}
                      onSubmit={stepConfig[step].onSubmit}
                      loading={loading}
                      inputFields={stepConfig[step].inputFields}
                      submitting={submitting}
                      buttonText={stepConfig[step].buttonText}
                      is_block={true}
                      col={12}
                      buttonCol={12}
                      is_passwordHide={false}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="simple-footer text-center mt-4">
              Copyright &copy; 2024 Bitanica Games. All rights reserved.
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;